import { useState } from 'react';
import { Sparkles, Loader2, Calendar } from 'lucide-react';
import { api } from '../lib/api';

const EXAMPLES = ['1 vidéo par jour', '2 vidéos par jour', '1 vidéo tous les 2 jours', '3 vidéos par semaine'];

export const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const rhythmEndDate = (start, count, r) => (start && r ? addDays(start, Math.floor((Math.max(1, count) - 1) / r.per_slot) * r.every_days) : '');

export function DateModeToggle({ mode, setMode }) {
  const opt = (id, label) => (
    <button
      type="button"
      data-testid={`wizard-date-mode-${id}`}
      onClick={() => setMode(id)}
      style={{
        flex: 1, padding: '8px 12px', borderRadius: 9, border: 'none', cursor: 'pointer',
        background: mode === id ? 'linear-gradient(180deg, #5BBEFC 0%, #3E7BFA 100%)' : 'transparent',
        color: mode === id ? '#0B1330' : 'var(--t3)',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        transition: 'background-color 0.14s ease, color 0.14s ease',
      }}
    >{id === 'rhythm' ? <Sparkles size={13} /> : <Calendar size={13} />}{label}</button>
  );
  return (
    <div style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 12, background: 'var(--sf2)', border: '1px solid var(--bd)' }}>
      {opt('manual', 'Dates manuelles')}
      {opt('rhythm', 'Rythme IA')}
    </div>
  );
}

export function RhythmInput({ rhythm, setRhythm, startDate, videoCount }) {
  const [text, setText] = useState(rhythm?.label || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const parse = async (value) => {
    const v = (value ?? text).trim();
    if (!v) return;
    setBusy(true); setError('');
    try {
      const r = await api.post('/projects/parse-rhythm', { text: v });
      setRhythm(r.data);
    } catch (e) {
      setRhythm(null);
      setError(e?.response?.data?.detail || 'Rythme pas compris');
    } finally {
      setBusy(false);
    }
  };

  const end = rhythmEndDate(startDate, videoCount, rhythm);
  return (
    <div data-testid="wizard-rhythm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          data-testid="wizard-rhythm-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); parse(); } }}
          onBlur={() => text.trim() && text.trim() !== rhythm?.label && parse()}
          placeholder="Ex : 2 vidéos par jour, 1 vidéo tous les 2 jours…"
          style={{ flex: 1, fontFamily: 'Lexend', fontSize: 13 }}
        />
        <button
          type="button"
          data-testid="wizard-rhythm-apply"
          onClick={() => parse()}
          disabled={busy || !text.trim()}
          style={{ padding: '0 14px', borderRadius: 10, border: 'none', cursor: busy ? 'wait' : 'pointer', background: 'linear-gradient(135deg, #A78BFA 0%, #7C5CF5 100%)', color: '#fff', fontFamily: 'Sora', fontWeight: 800, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >{busy ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />} Calculer</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {EXAMPLES.map((ex) => (
          <button key={ex} type="button" data-testid={`wizard-rhythm-example-${ex.replace(/\s+/g, '-')}`} onClick={() => { setText(ex); parse(ex); }} style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#C4B5FD', fontFamily: 'Sora', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>{ex}</button>
        ))}
      </div>
      {error && <div data-testid="wizard-rhythm-error" style={{ fontFamily: 'Lexend', fontSize: 12, color: '#FF6B6B' }}>{error}</div>}
      {rhythm && (
        <div data-testid="wizard-rhythm-preview" style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(167,139,250,0.10)', border: '1.5px solid rgba(167,139,250,0.32)', fontFamily: 'Lexend', fontSize: 12.5, color: '#C4B5FD', lineHeight: 1.5 }}>
          <strong>{rhythm.label}</strong>
          {startDate
            ? <> · {videoCount} vidéo{videoCount > 1 ? 's' : ''} du {new Date(`${startDate}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au <strong>{new Date(`${end}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</strong> (deadline auto)</>
            : ' · choisis la date de début'}
        </div>
      )}
    </div>
  );
}
